'use client';
import { useState } from 'react';
import { useT } from '../../lib/i18n';
import { Icon } from '../Icon';
import { Working } from '../Working';
import { toast } from '../Toast';
import { confirmLayer } from '../Alert';
import { maskUid, type BoardComment } from './types';

/**
 * 댓글 — 가이드 `notice-view.html` 아래쪽(`qna-comments` + `qna-comment-write`).
 *
 * 서버가 단건 응답에 댓글을 함께 준다. 그 값으로 시작하고, 쓰거나 지우면 그 자리에서 고친다
 * (다시 받아오지 않는다 — 서버가 방금 만든 것을 돌려주므로 화면이 이미 안다).
 *
 * **권한은 서버가 판정해서 알려 준다**(2026-09-01 사용자 제보로 고침) — 쓰기 칸은
 * `canComment`(게시판 설정 `commentRole`), 지우기는 댓글마다 `canDelete`(본인·관리자)다.
 * 전에는 화면이 그걸 보지 않아 **관리자만 쓸 수 있는 게시판에서도 댓글 칸이 보였다**.
 * 화면이 권한을 스스로 셈하면 서버와 어긋나므로, 판정은 서버가 하고 화면은 받아 쓴다.
 */
const ICON_X = 'x';
const ICON_EDIT = 'pencil';
const MAX_LEN = 400;

export function BoardComments({ postId, initial, canComment, meName, meUid }: {
  postId: number;
  initial: BoardComment[];
  canComment: boolean;
  /**
   * 지금 로그인한 사람 — **누구로 쓰는지 보여 주기 위해서다**(2026-09-01 사용자 제보).
   * 관리자 계정으로 로그인한 채 회원인 줄 알고 댓글을 달아, 남의 이름으로 쓴 것처럼 보였다.
   * 쓰기 전에 이름이 보이면 그 자리에서 알아챈다.
   */
  meName: string | null;
  meUid: string | null;
}) {
  const t = useT();
  const [rows, setRows] = useState<BoardComment[]>(initial);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  /** 고치는 중인 댓글 — 그 자리에서 바꾼다(댓글형 게시판의 글 수정과 같은 결) */
  const [editing, setEditing] = useState<number | null>(null);
  const [editText, setEditText] = useState('');

  const add = async () => {
    // 잠그지 않는다 — 누르면 까닭을 말한다(2026-09-01 전역 규칙)
    if (!text.trim()) { toast(t('bbs.needText'), 'error'); return; }
    setBusy(true);
    try {
      const res = await fetch(`/api/boards/posts/${postId}/comments`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ comment: text.trim() }),
      });
      const body = (await res.json().catch(() => ({}))) as Partial<BoardComment> & { message?: string };
      if (!res.ok) { toast(body.message ?? t('acc.err.network'), 'error'); return; }
      /*
       * 서버가 **만든 댓글을 통째로** 준다(2026-09-01). 그래도 못 받은 칸이 있으면 방금 쓴 값을
       * 채워 둔다 — 화면에 빈 자리를 남기느니 아는 값을 그리는 편이 낫다(새로 고치면 서버 값이다).
       */
      const now = new Date().toISOString();
      setRows((list) => [...list, {
        id: body.id ?? Date.now(),
        writer: body.writer ?? null,
        name: body.name ?? null,
        comment: body.comment ?? text.trim(),
        createdAt: body.createdAt ?? now,
        canEdit: body.canEdit ?? true,
        canDelete: body.canDelete ?? true,
      }]);
      setText('');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id: number) => {
    if (!editText.trim()) { toast(t('bbs.needText'), 'error'); return; }
    setBusy(true);
    try {
      const res = await fetch(`/api/boards/comments/${id}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ comment: editText.trim() }),
      });
      const body = (await res.json().catch(() => ({}))) as { comment?: string; message?: string };
      if (!res.ok) { toast(body.message ?? t('acc.err.network'), 'error'); return; }
      setRows((list) => list.map((c) => (c.id === id ? { ...c, comment: body.comment ?? editText.trim() } : c)));
      setEditing(null);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    if (!(await confirmLayer(t('bbs.commentDeleteAsk'), { kind: 'danger', okLabel: t('common.delete') }))) return;
    const res = await fetch(`/api/boards/comments/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      toast(body.message ?? t('acc.err.network'), 'error');
      return;
    }
    setRows((list) => list.filter((c) => c.id !== id));
  };

  return (
    <>
      <div className="qna-comments">
        <div className="qna-sec-bar-title mb12">
          <span className="lec-sec-bar"></span>
          <span className="fs14 fw-bold">{t('bbs.comment')} <span className="txt-accent">{rows.length}</span></span>
        </div>
        <div className="qna-comment-list">
          {rows.map((c) => (
            <div className="qna-comment-item" key={c.id}>
              {/* 이름을 모르면 괄호도 안 그린다 — 빈 `()` 가 남으면 고장으로 보인다 */}
              <span className="qna-comment-author">
                {c.name ?? c.writer ?? ''}{c.writer ? `(${maskUid(c.writer)})` : ''}
              </span>
              {editing === c.id ? (
                <span className="qna-comment-text">
                  <textarea className="qna-comment-textarea cmt-edit" maxLength={MAX_LEN}
                    value={editText} onChange={(e) => setEditText(e.target.value)} />
                  <span className="d-flex jc-end gap8 mt5">
                    <button className="action-btn action-cancel btn-sm" onClick={() => setEditing(null)}>{t('action.cancel')}</button>
                    <button className="action-btn action-submit btn-sm" disabled={busy} onClick={() => void saveEdit(c.id)}>
                      {busy ? <Working inline /> : null}{t('action.save')}
                    </button>
                  </span>
                </span>
              ) : (
                <span className="qna-comment-text">{c.comment}</span>
              )}
              <span className="qna-comment-date">{String(c.createdAt).slice(0, 19).replace('T', ' ')}</span>
              {c.canEdit && editing !== c.id && (
                <button type="button" className="qna-comment-del" aria-label={t('action.edit')}
                  onClick={() => { setEditing(c.id); setEditText(c.comment ?? ''); }}><Icon name={ICON_EDIT} /></button>
              )}
              {c.canDelete && editing !== c.id && (
                <button type="button" className="qna-comment-del" aria-label={t('common.delete')}
                  onClick={() => void remove(c.id)}><Icon name={ICON_X} /></button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 댓글 칸은 **쓸 수 있는 사람에게만** — 못 쓰는 사람에게 보이면 쓰고 나서야 막힌다 */}
      {canComment && (
      <div className="qna-comment-write mb20">
        <textarea className="qna-comment-textarea" maxLength={MAX_LEN} value={text}
          placeholder={t('bbs.commentPh')} onChange={(e) => setText(e.target.value)} />
        <div className="qna-comment-footer">
          {/* 누구로 쓰는지 — 계정을 착각한 채 쓰는 일을 막는다(2026-09-01) */}
          <span className="qna-comment-footer-label">
            {meUid ? t('bbs.writeAs', { who: `${meName ?? meUid}(${maskUid(meUid)})` }) : t('bbs.writeComment')}
          </span>
          <div className="d-flex ai-center gap10">
            <span className="qna-count">{t('bbs.charCount', { n: text.length, max: MAX_LEN })}</span>
            <button className="action-btn action-submit btn-sm" disabled={busy} onClick={() => void add()}>
              {busy ? <Working inline /> : null}{t('bbs.submit')}
            </button>
          </div>
        </div>
      </div>
      )}
    </>
  );
}
